import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useSiteData } from "@/lib/use-site-data";

export const Route = createFileRoute("/contacto")({
  head: () => ({
    meta: [
      { title: "Contacto · El Pajar de Benito" },
      { name: "description", content: "Cómo llegar y cómo contactar con El Pajar de Benito en El Ordial, Guadalajara." },
      { property: "og:title", content: "Contacto · El Pajar de Benito" },
      { property: "og:description", content: "Escríbenos por email o WhatsApp y cómo llegar a El Pajar de Benito en El Ordial (Guadalajara), a 2 horas de Madrid." },
      { property: "og:url", content: "/contacto" },
    ],
    links: [{ rel: "canonical", href: "https://elpajardebenito.es/contacto" }],
  }),
  component: Contacto,
});

function Contacto() {
  const { config } = useSiteData();
  const waNumber = (config.whatsapp ?? "").replace(/\D/g, "");
  const fullAddress = [
    config.nombre_comercial || "El Pajar de Benito",
    config.direccion,
    config.codigo_postal,
    config.ciudad,
    config.pais,
  ]
    .filter(Boolean)
    .join(", ");
  const mapsQuery = encodeURIComponent(fullAddress);
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${mapsQuery}`;
  const viewUrl = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;
  const wazeUrl = `https://www.waze.com/ul?q=${mapsQuery}&navigate=yes`;
  const appleMapsUrl = `https://maps.apple.com/?daddr=${mapsQuery}`;
  return (
    <section className="mx-auto max-w-5xl px-4 py-20 md:px-6 md:py-28">
      <p className="text-xs uppercase tracking-[0.28em] text-accent">Contacto</p>
      <h1 className="mt-3 font-serif text-4xl text-foreground md:text-5xl">Hablamos</h1>
      <p className="mt-5 max-w-2xl text-muted-foreground">{config.contacto_intro}</p>
      <div className="mt-12 grid gap-10 md:grid-cols-2">
        <div className="space-y-6">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-foreground/70">Email</p>
            <a href={`mailto:${config.email_contacto}`} className="mt-1 block text-lg text-primary">{config.email_contacto}</a>
          </div>
          {waNumber && (
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-foreground/70">WhatsApp</p>
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener" className="mt-1 block text-lg text-primary">{config.telefono_contacto || `+${waNumber}`}</a>
            </div>
          )}
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-foreground/70">Dirección</p>
            <address className="mt-1 not-italic text-foreground/90">
              {config.direccion}<br />
              {config.codigo_postal} {config.ciudad}<br />
              {config.pais}
            </address>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-foreground/70">Cómo llegar</p>
            <p className="mt-1 text-muted-foreground">A 2 horas de Madrid por la A-2 / CM-1006. Recomendamos coche para moverse por la zona.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 rounded-sm bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                Cómo llegar en Google Maps
              </a>
              <a
                href={wazeUrl}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Waze
              </a>
              <a
                href={appleMapsUrl}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Apple Maps
              </a>
              <a
                href={viewUrl}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Abrir ubicación
              </a>
            </div>
          </div>
        </div>
        <div className="overflow-hidden rounded-sm border border-border">
          <iframe
            title={`Mapa de ${config.nombre_comercial}`}
            src={`https://www.google.com/maps?q=${mapsQuery}&output=embed&z=15`}
            className="h-[400px] min-h-[400px] w-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </div>
      <p className="mt-8 text-xs text-muted-foreground">
        Al escribirnos, tratamos tus datos para responder a tu consulta conforme a nuestra{" "}
        <Link to="/legal/privacidad" className="underline hover:text-primary">política de privacidad</Link>.
        Responsable: {config.nombre_fiscal || "CASA JUANA Y LUCIO S.L."} · {config.nif || "B85745362"}.
      </p>
    </section>
  );
}